import { createFileRoute, Link } from "@tanstack/react-router";
import { HiddenSeoContext } from "@/components/site/HiddenSeoContext";
import { SiteShell } from "@/components/site/SiteShell";
import { ServiceDetailSection } from "@/components/site/ServiceDetailSection";
import { SERVICE_DETAIL_CONTENT } from "@/lib/service-detail-content";
import { SectionHeading } from "@/components/site/SectionHeading";
import { webPageScript } from "@/lib/webpage-jsonld";


export const Route = createFileRoute("/privacy")({
  head: () => ({
    meta: [
      { title: "Privacy & Data Protection Policy | Online Tax VAT Bangladesh" },
      { name: "description", content: "How Online Tax VAT Bangladesh collects, stores and protects personal information and service documents." },
      { property: "og:title", content: "Privacy Policy — Online Tax VAT Bangladesh" },
      { property: "og:description", content: "How Online Tax VAT Bangladesh collects, stores and protects your personal and tax data." },
      { property: "og:url", content: "https://onlinetaxvat.com/privacy" },
      { property: "og:type", content: "website" },
    ],
    links: [{ rel: "canonical", href: "https://onlinetaxvat.com/privacy" }],
    scripts: [webPageScript({
      path: "/privacy",
      name: "Privacy Policy — Online Tax VAT Bangladesh",
      description: "How Online Tax VAT Bangladesh collects, stores and protects personal information and service documents.",
    })],
  }),
  component: PrivacyPage,
});

function PrivacyPage() {
  return (
    <SiteShell>
      <section className="container-page py-12">
        <h1 className="sr-only">Privacy Policy — Online Tax VAT Bangladesh</h1>
        <SectionHeading
          eyebrow="Legal"
          title="Privacy Policy"
          description="Last updated: 16 June 2026"
        />
        <article className="prose prose-slate dark:prose-invert mt-8 max-w-3xl">
          <p>
            Online Tax VAT Bangladesh respects your right to privacy. This Privacy
            Policy explains what personal and tax information we collect when you use
            our VAT registration (BIN), VAT return (Mushak 9.1), TIN certificate,
            Income Tax return, visa tax file and consultancy services, why we collect
            it, how it is stored, and the choices you have over it. This policy works
            together with our <Link to="/terms">Terms of Service</Link>, the
            <Link to="/faq"> FAQ</Link> and the detailed
            <Link to="/services"> service catalogue</Link>. Our practices follow the
            National Board of Revenue (NBR) record-keeping rules and Bangladesh&apos;s
            data protection conventions.
          </p>
          <h2>1. Information we collect</h2>
          <p>
            We collect contact details (name, phone, email, business name), tax
            identifiers (TIN, BIN, NID, trade licence number), uploaded documents
            required for VAT and Income Tax filing (bank statements, sales registers,
            salary sheets, treasury challans), and payment references. We never
            collect more than what is needed to deliver the service you ordered on
            the <Link to="/packages">Packages</Link> page.
          </p>
          <h2>2. How we use your data</h2>
          <p>
            Your data is used solely to prepare and submit VAT/Tax returns to NBR,
            communicate filing status, issue invoices and tax receipts, meet
            statutory record-keeping obligations, and improve the quality of our
            consultancy. We do not use your tax data for marketing or profiling.
          </p>
          <h2>3. Data sharing</h2>
          <p>
            We do not sell your data. Documents are shared only with authorised tax
            consultants assigned to your case and, where legally required, with the
            National Board of Revenue (NBR), the Office of the Commissioner of Taxes,
            or a court of competent jurisdiction in Bangladesh.
          </p>
          <h2>4. Storage &amp; security</h2>
          <p>
            Files are stored on encrypted cloud infrastructure with restricted access
            and audited backups. All site traffic is served over HTTPS/SSL with
            modern TLS ciphers, and document downloads use signed, time-limited URLs
            so that links cannot be reused once they expire.
          </p>
          <h2>5. Data retention</h2>
          <p>
            Tax returns and supporting documents are retained for the minimum period
            required by NBR (typically six years for VAT records and five years for
            income tax records). After that period, files are securely deleted unless
            you ask us to keep them for ongoing consultancy.
          </p>
          <h2>6. Your rights</h2>
          <p>
            You may request a copy, correction or deletion of your data at any time by
            emailing our support team via the <Link to="/contact">Contact</Link> page. We
            respond to verified requests within seven working days. Deletion requests
            are honoured except where the file is part of a statutory NBR submission
            that must legally be preserved.
          </p>
          <h2>7. Cookies &amp; analytics</h2>
          <p>
            We use a small number of first-party cookies to keep you signed in and to
            measure traffic to popular guides on the
            <Link to="/knowledge-hub"> Knowledge Hub</Link> and the
            <Link to="/blog"> Blog</Link>. We do not use cookies for cross-site
            advertising, and we never share identifiable browsing data with third
            parties.
          </p>
          <h2>8. Contact</h2>
          <p>
            For privacy questions, contact us via the
            <Link to="/contact"> Contact</Link> page or email our compliance desk. We aim
            to acknowledge every privacy enquiry within two working days.
          </p>
          <h2>Related reading</h2>
          <ul>
            <li><Link to="/terms">Terms of Service</Link></li>
            <li><Link to="/faq">Frequently Asked Questions</Link></li>
            <li><Link to="/services">All VAT &amp; Tax services</Link></li>
            <li><Link to="/knowledge-hub">Knowledge Hub — guides &amp; circulars</Link></li>
            <li><Link to="/blog">Latest blog updates</Link></li>
            <li><Link to="/contact">Contact our team</Link></li>
          </ul>
        </article>
        <div className="container-page"><ServiceDetailSection content={SERVICE_DETAIL_CONTENT["privacy"]} /></div>
      </section>
      <HiddenSeoContext topic="privacy" />
    </SiteShell>
  );
}
